/**
 * PLC 测试运行 Hook
 * 
 * 提供测试用例的管理和执行功能
 */

import { useState, useCallback, useMemo } from 'react';
import type { PlcLanguageType } from '../types';
import type {
  TestCase,
  TestCaseResult,
  PlcVariable,
  CodeParseResult,
  TestValue
} from '../types/testTypes';
import { 
  generateTestId, 
  createEmptyTestCase,
  getDefaultValueForType 
} from '../types/testTypes';
import { parseVariables } from '../services/plcCodeService';
import { PlcSimulatorService } from '../services/plcSimulatorService';

// ============================================
// 类型定义
// ============================================

export interface UsePlcTestRunnerOptions {
  /** 初始代码 */
  readonly initialCode?: string;
  /** 初始语言 */
  readonly initialLanguage?: PlcLanguageType;
}

export interface UsePlcTestRunnerReturn {
  /** 测试用例列表 */
  readonly testCases: readonly TestCase[];
  /** 测试结果映射 */
  readonly results: Readonly<Record<string, TestCaseResult>>;
  /** 解析出的变量信息 */
  readonly parseResult: CodeParseResult | null;
  /** 是否正在运行 */
  readonly isRunning: boolean;
  /** 当前选中的用例 ID */
  readonly selectedCaseId: string | null;
  
  // 操作方法
  /** 解析代码变量 */
  readonly parseCode: (code: string, language: PlcLanguageType) => void;
  /** 添加测试用例 */
  readonly addTestCase: () => void;
  /** 删除测试用例 */
  readonly deleteTestCase: (id: string) => void;
  /** 更新测试用例 */
  readonly updateTestCase: (id: string, updates: Partial<TestCase>) => void;
  /** 更新测试输入值 */
  readonly updateInputValue: (caseId: string, variableName: string, value: string) => void;
  /** 更新期望输出值 */
  readonly updateExpectedOutput: (caseId: string, variableName: string, value: string) => void;
  /** 运行单个测试用例 */
  readonly runTestCase: (id: string, code: string, language: PlcLanguageType) => Promise<void>;
  /** 运行所有测试用例 */
  readonly runAllTestCases: (code: string, language: PlcLanguageType) => Promise<void>;
  /** 选中测试用例 */
  readonly selectTestCase: (id: string | null) => void;
  /** 清空所有用例 */
  readonly clearAllTestCases: () => void;
  /** 复制测试用例 */
  readonly duplicateTestCase: (id: string) => void;
}

// ============================================
// Hook 实现
// ============================================

export const usePlcTestRunner = (
  options: UsePlcTestRunnerOptions = {}
): UsePlcTestRunnerReturn => {
  // 状态
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [results, setResults] = useState<Record<string, TestCaseResult>>({});
  const [parseResult, setParseResult] = useState<CodeParseResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  
  // 解析代码变量
  const parseCode = useCallback((code: string, language: PlcLanguageType): void => {
    const result = parseVariables(code, language);
    // console.log('parseVariables', result);
    
    setParseResult(result);
    
    // 代码变化时，始终清空旧的测试状态，基于新变量重建
    setResults({});
    
    if (result.success) {
      const newCase = createEmptyTestCase(result.inputs, result.outputs);
      setTestCases([newCase]);
      setSelectedCaseId(newCase.id);
    } else {
      setTestCases([]);
      setSelectedCaseId(null);
    }
  }, []);
  
  // 添加测试用例
  const addTestCase = useCallback((): void => {
    if (!parseResult) return;
    
    const newCase = createEmptyTestCase(parseResult.inputs, parseResult.outputs);
    setTestCases(prev => [...prev, newCase]);
    setSelectedCaseId(newCase.id);
  }, [parseResult]);
  
  // 删除测试用例
  const deleteTestCase = useCallback((id: string): void => {
    setTestCases(prev => prev.filter(tc => tc.id !== id));
    setResults(prev => {
      const newResults = { ...prev };
      delete newResults[id];
      return newResults;
    });
    
    if (selectedCaseId === id) {
      setSelectedCaseId(null);
    }
  }, [selectedCaseId]);
  
  // 更新测试用例
  const updateTestCase = useCallback((id: string, updates: Partial<TestCase>): void => {
    setTestCases(prev => prev.map(tc => 
      tc.id === id ? { ...tc, ...updates } : tc
    ));
  }, []);
  
  // 更新输入值
  const updateInputValue = useCallback((
    caseId: string, 
    variableName: string, 
    value: string
  ): void => {
    setTestCases(prev => prev.map(tc => {
      if (tc.id !== caseId) return tc;
      
      const newInputs = tc.inputs.map(input =>
        input.variableName === variableName
          ? { ...input, value }
          : input
      );
      
      return { ...tc, inputs: newInputs, status: 'pending' as const };
    }));
    
    // 清除该用例的测试结果
    setResults(prev => {
      const newResults = { ...prev };
      delete newResults[caseId];
      return newResults;
    });
  }, []);
  
  // 更新期望输出值
  const updateExpectedOutput = useCallback((
    caseId: string,
    variableName: string,
    value: string
  ): void => {
    setTestCases(prev => prev.map(tc => {
      if (tc.id !== caseId) return tc;
      
      const newOutputs = tc.expectedOutputs.map(output =>
        output.variableName === variableName
          ? { ...output, value }
          : output
      );
      
      return { ...tc, expectedOutputs: newOutputs, status: 'pending' as const };
    }));
    
    // 清除该用例的测试结果
    setResults(prev => {
      const newResults = { ...prev };
      delete newResults[caseId];
      return newResults;
    });
  }, []);
  
  // 运行单个测试用例
  const runTestCase = useCallback(async (
    id: string,
    code: string,
    language: PlcLanguageType
  ): Promise<void> => {
    const testCase = testCases.find(tc => tc.id === id);
    if (!testCase || !parseResult) return;
    
    setIsRunning(true);
    
    // 更新状态为运行中
    setTestCases(prev => prev.map(tc =>
      tc.id === id ? { ...tc, status: 'running' as const } : tc
    ));
    
    // 异步执行（允许 UI 更新）
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      const result = PlcSimulatorService.runTestCase(
        testCase,
        code,
        language,
        parseResult.variables
      );
      
      // 更新结果
      setResults(prev => ({ ...prev, [id]: result }));
      
      // 更新状态
      setTestCases(prev => prev.map(tc =>
        tc.id === id
          ? { 
              ...tc, 
              status: result.passed ? 'passed' : 'failed',
              lastRunAt: new Date().toISOString()
            }
          : tc
      ));
    } catch (error) {
      setTestCases(prev => prev.map(tc =>
        tc.id === id ? { ...tc, status: 'error' as const } : tc
      ));
    } finally {
      setIsRunning(false);
    }
  }, [testCases, parseResult]);
  
  // 运行所有测试用例
  const runAllTestCases = useCallback(async (
    code: string,
    language: PlcLanguageType
  ): Promise<void> => {
    if (!parseResult || testCases.length === 0) return;
    
    setIsRunning(true);
    
    // 标记所有为运行中
    setTestCases(prev => prev.map(tc => ({ ...tc, status: 'running' as const })));
    
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      const allResults = PlcSimulatorService.runAllTestCases(
        testCases,
        code,
        language,
        parseResult.variables
      );
      
      // 更新所有结果
      const newResults: Record<string, TestCaseResult> = {};
      allResults.forEach((result, id) => {
        newResults[id] = result;
      });
      setResults(newResults);
      
      // 更新所有状态
      setTestCases(prev => prev.map(tc => {
        const result = allResults.get(tc.id);
        return {
          ...tc,
          status: result?.passed ? 'passed' : 'failed',
          lastRunAt: new Date().toISOString()
        };
      }));
    } finally {
      setIsRunning(false);
    }
  }, [testCases, parseResult]);
  
  // 选中测试用例
  const selectTestCase = useCallback((id: string | null): void => {
    setSelectedCaseId(id);
  }, []);
  
  // 清空所有用例
  const clearAllTestCases = useCallback((): void => {
    setTestCases([]);
    setResults({});
    setSelectedCaseId(null);
  }, []);
  
  // 复制测试用例
  const duplicateTestCase = useCallback((id: string): void => {
    const sourceCase = testCases.find(tc => tc.id === id);
    if (!sourceCase) return;
    
    const newCase: TestCase = {
      ...sourceCase,
      id: generateTestId(),
      name: `${sourceCase.name} (副本)`,
      status: 'pending',
      createdAt: new Date().toISOString(),
      lastRunAt: undefined
    };
    
    setTestCases(prev => [...prev, newCase]);
    setSelectedCaseId(newCase.id);
  }, [testCases]);
  
  // 统计信息
  const stats = useMemo(() => {
    const total = testCases.length;
    const passed = testCases.filter(tc => tc.status === 'passed').length;
    const failed = testCases.filter(tc => tc.status === 'failed').length;
    const pending = testCases.filter(tc => tc.status === 'pending').length;
    
    return { total, passed, failed, pending };
  }, [testCases]);
  
  return {
    testCases,
    results,
    parseResult,
    isRunning,
    selectedCaseId,
    parseCode,
    addTestCase,
    deleteTestCase,
    updateTestCase,
    updateInputValue,
    updateExpectedOutput,
    runTestCase,
    runAllTestCases,
    selectTestCase,
    clearAllTestCases,
    duplicateTestCase
  };
};

export default usePlcTestRunner;